import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { InputHTMLAttributes } from 'react';
import { useDisclosure } from '../../../hooks/useDisclosure';
import { FieldError, FormProvider, useController, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import { updatePassword } from '../../../api/users';
import useNotify from '../../../hooks/useNotify';
import styled from 'styled-components';
import { IoIosArrowBack } from 'react-icons/io';
import { AiFillCheckCircle, AiOutlineCheckCircle, AiFillEye, AiFillEyeInvisible } from 'react-icons/ai';
import { Button, useScreenQuery } from 'playup-web-core';
import usePasswordCheck from '../../../hooks/usePasswordCheck';
interface Inputs {
	password: string;
	new_password: string;
	new_confirm_password: string;
}

const ResetPasswordForm = () => {
	const schema = yup.object().shape({
		password: yup.string().required('Current password is required'),
		new_password: yup.string().required('New password is required'),
		new_confirm_password: yup
			.string()
			.test('passwords-match', 'Passwords must match', function (value) {
				return this.parent.new_password === value;
			})
			.required('Please retype your password.'),
	});
	const { isSmall } = useScreenQuery();
	const { isOpen, onClose, onToggle } = useDisclosure();
	const methods = useForm<Inputs>({
		resolver: yupResolver(schema),
		mode: 'all',
		defaultValues: {
			password: '',
			new_password: '',
			new_confirm_password: '',
		},
	});
	const { refreshPortalUser } = React.useContext(PortalSessionContext);
	const notify = useNotify();
	const onSubmit = React.useCallback(
		async (data: Inputs) => {
			try {
				await updatePassword({
					new_confirm_password: data.new_confirm_password,
					new_password: data.new_password,
					password: data.password,
				});
				notify({
					type: 'success',
					title: 'Success',
					message: 'Your password has been updated!',
					timeout: 2000,
				});
				refreshPortalUser();
				onClose();
			} catch (error) {
				const { errors } = error as {
					errors: APIError | undefined | null;
				};
				if (errors) {
					Object.keys(errors).forEach((key) => {
						methods.setError(key as keyof Inputs, {
							type: 'manual',
							message: errors[key][0],
						});
					});
				}
			}
		},
		[methods, notify, onClose, refreshPortalUser]
	);
	return (
		<FormProvider {...methods}>
			<Container open={isOpen} isSmall={isSmall}>
				<ResetPasswordButton onClick={onToggle}>
					{isOpen && <IoIosArrowBack style={{ position: 'absolute', left: 10, fontSize: 18, fontWeight: 'bold' }} />}
					Reset Password
				</ResetPasswordButton>

				<Content>
					<Title>Current Password</Title>
					<ControlledInput placeholder="Enter your current password" name="password" />
					<Title>New Password</Title>
					<ControlledInput placeholder="Enter your new password" name="new_password" />
					<Title>Confirm New Password</Title>
					<ControlledInput placeholder="Enter your new password" name="new_confirm_password" />
					<ResetPasswordButton
						onClick={methods.handleSubmit(onSubmit)}
						style={{
							backgroundColor: 'black',
							borderRadius: 5,
							marginTop: 14,
						}}
					>
						<ResetPasswordButtonText
							style={{
								color: 'white',
							}}
						>
							Reset Password
						</ResetPasswordButtonText>
					</ResetPasswordButton>
				</Content>
			</Container>
		</FormProvider>
	);
};
interface ControlledInputProps extends InputHTMLAttributes<HTMLInputElement> {
	name: 'password' | 'new_password' | 'new_confirm_password';
}
const ControlledInput = ({ name, ...props }: ControlledInputProps) => {
	const [secureTextEntry, setSecureTextEntry] = React.useState(true);
	const onTogleSecureTextEntry = React.useCallback(() => {
		setSecureTextEntry((prev) => !prev);
	}, []);
	const {
		field,
		fieldState: { error },
	} = useController({
		name: name,
	});
	const [isFocused, setIsFocused] = React.useState(false);
	const onBlur = React.useCallback(() => {
		field.onBlur();
		setIsFocused(false);
	}, [field]);
	const onFocus = React.useCallback(() => {
		setIsFocused(true);
	}, []);
	const password = field.value;
	const { includeUpperAndLowerCase, includeAtLeastOneNumber, hasEightOrMoreCharacters } = usePasswordCheck(
		password ?? ''
	);
	const { isSmall } = useScreenQuery();
	return (
		<PasswordSection>
			{isFocused && name === 'new_password' && (
				<>
					<PasswordTooltip isSmall={isSmall}>
						<PasswordRequirement>
							<CheckIcon name="check-circle" checked={includeUpperAndLowerCase} /> Includes lower and upper case
						</PasswordRequirement>
						<PasswordRequirement>
							<CheckIcon name="check-circle" checked={includeAtLeastOneNumber} /> Includes at least one number
						</PasswordRequirement>
						<PasswordRequirement>
							<CheckIcon name="check-circle" checked={hasEightOrMoreCharacters} /> Password must be 8 or more characters
						</PasswordRequirement>
					</PasswordTooltip>
					<PasswordTooltipArrow isSmall={isSmall} />
				</>
			)}
			<InputContainer>
				<input
					onFocus={onFocus}
					value={field.value}
					onChange={field.onChange}
					onBlur={onBlur}
					ref={field.ref}
					style={{ flex: 1 }}
					type={secureTextEntry ? 'password' : 'text'}
					{...props}
				/>
				<SecureTextButton onClick={onTogleSecureTextEntry}>
					{secureTextEntry ? <AiFillEye /> : <AiFillEyeInvisible />}
				</SecureTextButton>
			</InputContainer>
			<ErrorText>{error?.message}</ErrorText>
		</PasswordSection>
	);
};
const SecureTextButton = styled.button`
	font-size: 18px;
`;

interface CheckIconProps {
	name: string;
	checked: boolean;
}
const CheckIcon = ({ name, checked }: CheckIconProps) => {
	return <CheckIconContainer>{checked ? <AiFillCheckCircle /> : <AiOutlineCheckCircle />}</CheckIconContainer>;
};
const CheckIconContainer = styled.div`
	color: ${({ theme }) => theme.colors.success};
	margin-right: 7.5px;
`;
const ErrorText = styled.span`
	color: ${({ theme }) => theme.colors.danger};
	margin-left: 10px;
	font-size: 13px;
	margin-top: 5px;
`;
const PasswordSection = styled.div({
	position: 'relative',
});
const PasswordRequirement = styled.span(({ theme }) => ({
	marginTop: '5px',
	marginBottom: '5px',
	color: theme.colors.darkR,
	fontSize: '13px',
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'row',
}));
const PasswordTooltip = styled.div<{ isSmall?: boolean }>(({ theme, isSmall }) => ({
	position: 'absolute',
	bottom: '55px',
	left: isSmall ? 80 : 140,
	padding: '6px 12px',
	background: theme.colors.lightGreyR,
	borderRadius: '5px',
	zIndex: 50,
}));
const InputContainer = styled.div<{ error?: FieldError }>(({ theme, error }) => ({
	borderWidth: 1,
	borderColor: error ? theme.colors.danger : '#e2e8f0',
	height: 35,
	borderRadius: 4,
	flexDirection: 'row',
	paddingHorizontal: 11,
	alignItems: 'center',
	display: 'flex',
	justifyContent: 'space-between',
	padding: '0px 10px',
	color: theme.colors.black,
}));
const PasswordTooltipArrow = styled.div<{ isSmall: boolean }>(({ theme, isSmall }) => ({
	position: 'absolute',
	bottom: '45px',
	left: isSmall ? 100 : 160,
	width: 0,
	height: 0,
	borderStyle: 'solid',
	borderTopWidth: 10,
	borderRightWidth: 6,
	borderBottomWidth: 0,
	borderLeftWidth: 6,
	borderTopColor: theme.colors.lightGreyR,
	borderRightColor: 'transparent',
	borderBottomColor: 'transparent',
	borderLeftColor: 'transparent',
}));
const Container = styled.div<{ open: boolean; isSmall: boolean }>`
	background-color: white;
	width: 100%;
	border-radius: 5px;
	transition: all 0.6s ease-in-out;
	max-height: ${(props) => (props.open ? '100%' : props.isSmall ? '50px' : '60px')};
	overflow: hidden;
`;
const ResetPasswordButton = styled(Button)`
	position: relative;
`;
const ResetPasswordButtonText = styled.span``;
const Content = styled.div`
	display: flex;
	flex-direction: column;
	padding-left: 14px;
	padding-right: 14px;
	padding-bottom: 28px;
`;
const Title = styled.span`
	font-size: 14px;
	font-weight: 600;
	margin-top: 10px;
	margin-bottom: 10px;
	color: black;
`;
export default ResetPasswordForm;
